import { useEffect, useState } from 'react';
import { Button, Offcanvas, Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ProcessRunTaskType, TaskTemplateCardType, TaskTemplateType } from '@econtractor/types';
import Paginator from 'components/others/Paginator';
import SolarIcon from 'components/icons/SolarIcon';
import spinnerTitle from 'components/others/spinnerTitle';
import SpinnerBlock from 'components/others/SpinnerBlock';
import NewTaskTemplatePicker from '../../NewTask/NewTaskTemplatePicker';
import ProcessTasksTable from '../../ProcessDir/OffProcessDir/ProcessTasksTable';
import ProcessDiagram from '../../ProcessDir/processDiagram';
import { appendProcessTask, removeProcessTask, replaceProcessTask } from '../../ProcessDir/model/processTaskSnapshot';
import TaskCard from '../../TaskCard';
import { TaskProvider } from '../../TaskContext';
import OffTaskDir from '../../TaskDir/OffTaskDir';
import { useTaskDirList } from '../../TaskDir/model/useTaskDirList';
import ProcessRunProgress from '../ProcessRunProgress';
import { useOffProcessRun } from '../model/useOffProcessRun';
import ProcessRunGeneralFields from './ProcessRunGeneralFields';

const PaginatorAny = Paginator as any;

interface OffProcessRunProps {
    show: boolean;
    processId: number | null;
    onHide: () => void;
    onSaved: () => void;
}

type StepEditorState = { mode: 'create' } | { mode: 'edit'; task: ProcessRunTaskType } | null;

const OffProcessRun = ({ show, processId, onHide, onSaved }: OffProcessRunProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const form = useOffProcessRun({ show, processId, onHide, onSaved });
    const [diagramOpen, setDiagramOpen] = useState(false);
    const [stepEditor, setStepEditor] = useState<StepEditorState>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [openInstanceId, setOpenInstanceId] = useState<number | null>(null);
    const picker = useTaskDirList(pickerOpen && show);
    const draft = form.status === 'draft';
    const launched = !draft;

    useEffect(() => {
        if (show) return;
        setOpenInstanceId(null);
        setPickerOpen(false);
        setDiagramOpen(false);
        setStepEditor(null);
    }, [show]);

    const handleHide = () => {
        setPickerOpen(false);
        onHide();
    };

    const handlePick = (template: TaskTemplateType) => {
        form.setTasks((prev) => appendProcessTask(prev, template, false) as ProcessRunTaskType[]);
        setPickerOpen(false);
    };

    const handleStepSaved = (template?: TaskTemplateCardType) => {
        if (!template) return;
        const next =
            !stepEditor || stepEditor.mode === 'create'
                ? (appendProcessTask(form.tasks, template, true) as ProcessRunTaskType[])
                : (replaceProcessTask(form.tasks, stepEditor.task.dtId, template) as ProcessRunTaskType[]);
        form.setTasks(next);
        void form.persistTasks(next);
    };

    return (
        <>
            <Offcanvas
                show={show}
                onHide={handleHide}
                placement="end"
                backdrop
                backdropClassName="task-dir-offcanvas-backdrop"
                className="process-offcanvas"
            >
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title className="d-flex align-items-center gap-2">
                        {t('processRuns.titleEdit')}
                        <ProcessRunProgress status={form.status} done={form.progressDone} total={form.progressTotal} />
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                    <div className="off-task-dir-form d-flex flex-column h-100">
                        <div className="off-task-dir-content px-3 position-relative">
                            <SpinnerBlock show={form.loading || form.saving || form.launching} onlyBlur={form.saving || form.launching} />
                            <Tabs
                                variant="underline"
                                activeKey={form.activeTab}
                                onSelect={(tab) => tab && form.setActiveTab(tab)}
                                className="mb-3 invoice-tab border-bottom"
                            >
                                <Tab eventKey="general" title={t('tabGeneral')}>
                                    <ProcessRunGeneralFields
                                        categoryId={form.categoryId}
                                        name={form.name}
                                        description={form.description}
                                        showDescription={form.showDescription}
                                        launched={launched}
                                        disabled={form.locked || form.saving}
                                        taskFor={form.taskFor}
                                        lead={form.lead}
                                        object={form.object}
                                        onCategoryChange={form.setCategoryId}
                                        onNameChange={form.setName}
                                        onDescriptionChange={form.setDescription}
                                        onShowDescription={() => form.setShowDescription(true)}
                                        onTaskForChange={form.setTaskFor}
                                        onLeadChange={form.setLead}
                                        onObjectChange={form.setObject}
                                    />
                                </Tab>
                                <Tab
                                    eventKey="tasks"
                                    title={
                                        <>
                                            <SolarIcon name="list-ul" className="me-2 fs-14" />
                                            {t('processDir.tabTasks')}
                                        </>
                                    }
                                >
                                    <div className="d-flex gap-2 mb-3">
                                        {draft ? (
                                            <>
                                                <Button
                                                    size="sm"
                                                    variant="primary"
                                                    disabled={form.saving}
                                                    onClick={() => setPickerOpen(true)}
                                                >
                                                    <SolarIcon name="folder2-open" className="fs-14 me-1" />
                                                    {t('processDir.btnFromDir')}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="success"
                                                    disabled={form.saving}
                                                    onClick={() => setStepEditor({ mode: 'create' })}
                                                >
                                                    <SolarIcon name="plus" className="fs-14 me-1" />
                                                    {t('processDir.btnNewTask')}
                                                </Button>
                                            </>
                                        ) : null}
                                        <Button
                                            size="sm"
                                            variant="info"
                                            disabled={!form.tasks.length}
                                            onClick={() => setDiagramOpen(true)}
                                        >
                                            <SolarIcon name="share" className="fs-14 me-1" />
                                            {t('processDiagram.open')}
                                        </Button>
                                    </div> 
                                    <ProcessTasksTable 
                                        tasks={form.tasks}
                                        disabled={!draft || form.saving}
                                        launched={launched}
                                        onOpenInstance={launched ? setOpenInstanceId : undefined}
                                        onEdit={(task) => setStepEditor({ mode: 'edit', task: task as ProcessRunTaskType })}
                                        onRemove={(task) =>
                                            form.setTasks((prev) => removeProcessTask(prev, task) as ProcessRunTaskType[])
                                        }
                                        onDueChange={
                                            draft
                                                ? (dtId, patch) =>
                                                      form.setTasks((prev) =>
                                                          prev.map((task) => (task.dtId === dtId ? { ...task, ...patch } : task)),
                                                      )
                                                : undefined
                                        }
                                    />
                                </Tab>
                            </Tabs>
                        </div>
                        <div className="p-3 offcanvas-footer off-task-dir-footer">
                            <div className="d-flex justify-content-end gap-2">
                                {draft ? null : (
                                    <Button
                                        variant="secondary"
                                        className="min100px"
                                        disabled={form.saving || form.launching}
                                        onClick={handleHide}
                                    >
                                        <SolarIcon name="x-lg" className="fs-14 me-2" />
                                        {t('common:buttons.close')}
                                    </Button>
                                )}
                                {draft ? (
                                    <Button
                                        variant="success"
                                        className="min100px"
                                        disabled={form.saving || form.launching || form.loading}
                                        onClick={() => void form.handleLaunch()}
                                    >
                                        {spinnerTitle(
                                            <>
                                                <SolarIcon name="play-fill" className="fs-14 me-2" />
                                                {t('processRuns.btnLaunch')}
                                            </>,
                                            form.launching,
                                            t('processRuns.launching'),
                                        )}
                                    </Button>
                                ) : null}
                                {form.locked ? null : (
                                    <Button
                                        variant="info"
                                        className="min100px"
                                        disabled={form.saving || form.launching || form.loading}
                                        onClick={() => void form.handleSave()}
                                    >
                                        {spinnerTitle(
                                            <>
                                                <SolarIcon name="check2-all" className="fs-14 me-2" />
                                                {t('common:buttons.save')}
                                            </>,
                                            form.saving,
                                            t('common:buttons.saving'),
                                        )}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
            <Offcanvas
                show={pickerOpen && draft}
                onHide={() => setPickerOpen(false)}
                placement="end"
                backdrop
                backdropClassName="process-step-offcanvas-backdrop"
                className="process-step-offcanvas"
            >
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title>{t('processDir.pickerTitle')}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                    <div className="off-task-dir-form d-flex flex-column h-100">
                        <div className="off-task-dir-content px-3">
                            <NewTaskTemplatePicker
                                searchInput={picker.searchInput}
                                setSearchInput={picker.setSearchInput}
                                categoryId={picker.categoryId}
                                setFilterCategoryId={picker.setFilterCategoryId}
                                templates={picker.templates}
                                showSpinner={picker.showSpinner}
                                onSelect={handlePick}
                            />
                        </div>
                        {picker.paginator ? (
                            <div className="p-3 offcanvas-footer off-task-dir-footer">
                                <PaginatorAny
                                    paginator={picker.paginator}
                                    storageKey={picker.pageSizeKey}
                                    onChangePage={(nextPage: number) => picker.setPage(nextPage)}
                                    onChangeOnPage={(nextOnPage: number) => {
                                        picker.setOnPage(nextOnPage);
                                        picker.setPage(1);
                                    }}
                                />
                            </div>
                        ) : null}
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
            <ProcessDiagram
                show={diagramOpen}
                tasks={form.tasks}
                readOnly={!draft}
                onHide={() => setDiagramOpen(false)}
                onChange={(next) => form.setTasks(next as ProcessRunTaskType[])}
                onSavePositions={draft ? undefined : form.saveDiagramPositions}
            />
            <OffTaskDir
                show={Boolean(stepEditor) && draft}
                templateId={stepEditor?.mode === 'edit' ? stepEditor.task.templateId : null}
                forkFromTemplateId={
                    stepEditor?.mode === 'edit' && !stepEditor.task.owned ? stepEditor.task.templateId : null
                }
                disableRecurrence
                hideFromDirectory
                stacked
                onHide={() => setStepEditor(null)}
                onSaved={handleStepSaved}
            />
            <TaskProvider enabled={false}>
                <TaskCard
                    stacked
                    taskId={openInstanceId}
                    onHide={() => {
                        setOpenInstanceId(null);
                        void form.reload();
                    }}
                />
            </TaskProvider>
        </>
    );
};

export default OffProcessRun;
